import React, { FC, useEffect, useRef, useState } from 'react';
import { useTheme, useMediaQuery, ClickAwayListener, IconButton, TextField, Box } from '@maas-components/core';
import { Search } from '@maas-components/icons';
import SearchContainer from './SearchContainer';
import { Service } from '../../../types';
import useStyles from './styles';

type SearchFieldProps = {
    services: Service[];
    mode: 'dark' | 'light';
};

const SearchField: FC<SearchFieldProps> = ({ services, mode }) => {
    const classes = useStyles({ mode });
    const theme = useTheme();
    const [isResultContainerOpen, setResultContainerOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [recentServices, setRecentServices] = useState<string[]>([]);
    const searchContainerRef = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const localServices: string[] = JSON.parse(localStorage.getItem('maas-recent-services') || '[]') as string[];
        setRecentServices(localServices);
    }, []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    const handleFocus = (event: React.MouseEvent<Element, MouseEvent> | React.FocusEvent<Element>) => {
        setResultContainerOpen(true);
        event.stopPropagation();
    };

    const handleClick = (service: string) => {
        if (!recentServices.includes(service)) {
            const newRecentServicesNames =
                recentServices.length === 4 ? [service, ...recentServices.slice(0, 3)] : [service, ...recentServices];
            localStorage.setItem('maas-recent-services', JSON.stringify(newRecentServicesNames));
            setRecentServices(newRecentServicesNames);
        }
        setSearchValue('');
    };

    const onStopPropagation = (event: React.MouseEvent<HTMLInputElement>) => {
        event.stopPropagation();
    };

    const onClickAway = (event: React.MouseEvent<Document, MouseEvent>) => {
        setResultContainerOpen(false);
        event.stopPropagation();
    };

    return (
        <ClickAwayListener onClickAway={onClickAway}>
            <div onClick={onStopPropagation} className={classes.searchContainer} ref={searchContainerRef}>
                {isMobile ? (
                    <IconButton
                        className={classes.searchContainerIcon}
                        style={{ padding: 0 }}
                        onClick={handleFocus}
                        title="buscar serviço"
                    >
                        <Search id="maas-search-button" />
                    </IconButton>
                ) : (
                    <div onClick={onStopPropagation} className={classes.searchContainer}>
                        <TextField
                            variant="outlined"
                            hiddenLabel
                            placeholder="Procure aplicativos, serviços e o que mais precisar"
                            className={classes.searchField}
                            fullWidth
                            onChange={handleChange}
                            onFocus={handleFocus}
                            value={searchValue}
                            InputProps={{
                                classes: {
                                    input: classes.searchFieldInput,
                                    notchedOutline: classes.notchedOutline,
                                },
                                id: 'maas-search-input',
                                autoComplete: 'off',
                                startAdornment: (
                                    <Box className={classes.adornment} mr={1}>
                                        <Search />
                                    </Box>
                                ),
                            }}
                        />
                    </div>
                )}
                <SearchContainer
                    mode={mode}
                    handleServiceClick={handleClick}
                    services={services}
                    searchValue={searchValue}
                    isOpen={isResultContainerOpen}
                    setIsOpen={setResultContainerOpen}
                    handleSearchTextChange={handleChange}
                    recentServices={recentServices}
                    popperAnchor={searchContainerRef}
                />
            </div>
        </ClickAwayListener>
    );
};

export default SearchField;
